/**
 * CR-8193: the Stock Planning grid as a printed sheet.
 *
 * Every row the current filters match, not just the page on screen, in the
 * columns the user has visible (plus Supplier on the all-suppliers grid).
 *
 * Prints through the same mechanism as PrintableCallOffItems: portalled into
 * `document.body`, hidden on screen (`hidden print:block`), and revealed by
 * the @media print rule in `index.css` while the `stock-planning-print-active`
 * marker is on body — see PrintablePOSummary for why each part is needed.
 */
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { ColumnPreference, StockPlanningItem } from '../../../lib/api';
import { rowKey } from './allSuppliers';
import { blankCellText } from '../shared/directsColumns';

interface PrintableStockPlanningProps {
  items: StockPlanningItem[];
  columns: ColumnPreference[];
  /** The supplier filter as the user picked it, e.g. "All call-off suppliers". */
  supplierLabel: string;
  /** One line summarising the other filters in force. */
  filterSummary: string;
  formatDate: (date: string | Date) => string;
  /** Stamped on the footer, so a printed sheet says who ran it. */
  generatedBy: string | null;
}

/**
 * A cell as the grid shows it: blank Remaining to Produce once nothing is
 * left, Fulfilment as a percentage, a dash for no value.
 *
 * @param item - the row
 * @param columnId - the column
 * @returns the printed text
 */
export function printCellValue(item: StockPlanningItem, columnId: string): string {
  const value = item[columnId as keyof StockPlanningItem];
  if (value === undefined || value === null) return blankCellText(columnId);
  if (columnId === 'commodity_type' && value === '') return '—';
  const num = parseFloat(String(value));
  if (columnId === 'remaining_to_produce' && !isNaN(num) && num <= 0) return '';
  if (columnId === 'fulfilment_pct') return isNaN(num) ? '-' : `${num.toFixed(0)}%`;
  return String(value);
}

export default function PrintableStockPlanning({
  items, columns, supplierLabel, filterSummary, formatDate, generatedBy,
}: PrintableStockPlanningProps) {
  useEffect(() => {
    document.body.classList.add('stock-planning-print-active');
    return () => document.body.classList.remove('stock-planning-print-active');
  }, []);

  return createPortal(
    <div className="printable-stock-planning hidden print:block">
      <header style={{ marginBottom: '6mm' }}>
        <h1 style={{ fontSize: '14pt', margin: 0 }}>Stock Planning — {supplierLabel}</h1>
        <p style={{ margin: '2mm 0 0' }}>
          {items.length} {items.length === 1 ? 'row' : 'rows'}
          {filterSummary ? ` · ${filterSummary}` : ''}
        </p>
      </header>

      <table>
        <thead>
          <tr>
            {columns.map(col => <th key={col.column_id} align="left">{col.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {items.map(item => (
            <tr key={rowKey(item)}>
              {columns.map(col => (
                <td key={col.column_id}>{printCellValue(item, col.column_id)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      <footer style={{ marginTop: '6mm', fontSize: '8pt' }}>
        Generated {formatDate(new Date())}
        {generatedBy ? ` by ${generatedBy}` : ''}
      </footer>
    </div>,
    document.body,
  );
}
